var _excluded = ["oneOf"],
    _excluded2 = ["anyOf"];

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { ALL_OF_KEY, ANY_OF_KEY, CONST_KEY, DEFAULT_KEY, DEPENDENCIES_KEY, ONE_OF_KEY, PROPERTIES_KEY, REF_KEY } from "../constants";
import findSchemaDefinition from "../findSchemaDefinition";
import getClosestMatchingOption from "./getClosestMatchingOption";
import getDiscriminatorFieldFromSchema from "../getDiscriminatorFieldFromSchema";
import getSchemaType from "../getSchemaType";
import isObject from "../isObject";
import isFixedItems from "../isFixedItems";
import mergeDefaultsWithFormData from "../mergeDefaultsWithFormData";
import mergeObjects from "../mergeObjects";
import mergeSchemas from "../mergeSchemas";
import isMultiSelect from "./isMultiSelect";
import isSelect from "./isSelect";
import retrieveSchema, { resolveDependencies } from "./retrieveSchema";
import isConstant from "../isConstant";
import constIsAjvDataReference from "../constIsAjvDataReference";
import optionsList from "../optionsList";
import deepEquals from "../deepEquals";
var PRIMITIVE_TYPES = ['string', 'number', 'integer', 'boolean', 'null'];
/** Enum that indicates how `schema.additionalItems` should be handled by the `getInnerSchemaForArrayItem()` function.
 */

export var AdditionalItemsHandling;

(function (AdditionalItemsHandling) {
  AdditionalItemsHandling[AdditionalItemsHandling["Ignore"] = 0] = "Ignore";
  AdditionalItemsHandling[AdditionalItemsHandling["Invert"] = 1] = "Invert";
  AdditionalItemsHandling[AdditionalItemsHandling["Fallback"] = 2] = "Fallback";
})(AdditionalItemsHandling || (AdditionalItemsHandling = {}));
/** Given a `schema` will return an inner schema that for an array item. This is computed differently based on the
 * `additionalItems` enum and the value of `idx`. There are four possible returns:
 * 1. If `idx` is >= 0, then if `schema.items` is an array the `idx`th element of the array is returned if it is a valid
 *    index and not a boolean, otherwise it falls through to 3.
 * 2. If `schema.items` is not an array AND truthy and not a boolean, then `schema.items` is returned since it actually
 *    is a schema, otherwise it falls through to 3.
 * 3. If `additionalItems` is not `AdditionalItemsHandling.Ignore` and `schema.additionalItems` is an object, then
 *    `schema.additionalItems` is returned since it actually is a schema, otherwise it falls through to 4.
 * 4. {} is returned representing an empty schema
 *
 * @param schema - The schema from which to get the particular item
 * @param [additionalItems=AdditionalItemsHandling.Ignore] - How do we want to handle additional items?
 * @param [idx=-1] - Index, if non-negative, will be used to return the idx-th element in a `schema.items` array
 * @returns - The best fit schema object from the `schema` given the `additionalItems` and `idx` modifiers
 */


export function getInnerSchemaForArrayItem(schema) {
  var additionalItems = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : AdditionalItemsHandling.Ignore;
  var idx = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : -1;

  if (idx >= 0) {
    if (Array.isArray(schema.items) && idx < schema.items.length) {
      var item = schema.items[idx];

      if (typeof item !== 'boolean') {
        return item;
      }
    }
  } else if (schema.items && !Array.isArray(schema.items) && typeof schema.items !== 'boolean') {
    return schema.items;
  }

  if (additionalItems !== AdditionalItemsHandling.Ignore && isObject(schema.additionalItems)) {
    return schema.additionalItems;
  }

  return {};
}
/** Either add `computedDefault` at `key` into `obj` or not add it based on its value, the value of
 * `includeUndefinedValues`, the value of `emptyObjectFields` and if its parent field is required. Generally undefined
 * `computedDefault` values are added only when `includeUndefinedValues` is either true/"excludeObjectChildren". If `
 * includeUndefinedValues` is false and `emptyObjectFields` is not "skipDefaults", then non-undefined and non-empty-object
 * values will be added based on certain conditions.
 *
 * @param obj - The object into which the computed default may be added
 * @param key - The key into the object at which the computed default may be added
 * @param computedDefault - The computed default value that maybe should be added to the obj
 * @param includeUndefinedValues - Optional flag, if true, cause undefined values to be added as defaults.
 *          If "excludeObjectChildren", cause undefined values for this object and pass `includeUndefinedValues` as
 *          false when computing defaults for any nested object properties. If "allowEmptyObject", prevents undefined
 *          values in this object while allow the object itself to be empty and passing `includeUndefinedValues` as
 *          false when computing defaults for any nested object properties.
 * @param isParentRequired - The optional boolean that indicates whether the parent field is required
 * @param requiredFields - The list of fields that are required
 * @param experimental_defaultFormStateBehavior - Optional configuration object, if provided, allows users to override
 *        default form state behavior
 * @param isConst - Optional flag, if true, indicates that the schema has a const property defined, thus we should always return the computedDefault since it's coming from the const.
 */

function maybeAddDefaultToObject(obj, key, computedDefault, includeUndefinedValues, isParentRequired) {
  var requiredFields = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [];
  var experimental_defaultFormStateBehavior = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : {};
  var isConst = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : false;
  var _experimental_default = experimental_defaultFormStateBehavior.emptyObjectFields,
      emptyObjectFields = _experimental_default === void 0 ? 'populateAllDefaults' : _experimental_default;

  if (includeUndefinedValues || isConst) {
    // If includeUndefinedValues
    // Or if the schema has a const property defined, then we should always return the computedDefault since it's coming from the const.
    obj[key] = computedDefault;
  } else if (emptyObjectFields !== 'skipDefaults') {
    // If isParentRequired is undefined, then we are at the root level of the schema so defer to the requiredness of
    // the field key itself in the `requiredField` list
    var isSelfOrParentRequired = isParentRequired === undefined ? requiredFields.includes(key) : isParentRequired;

    if (isObject(computedDefault)) {
      // If emptyObjectFields 'skipEmptyDefaults' store computedDefault if it's a non-empty object(e.g. not {})
      if (emptyObjectFields === 'skipEmptyDefaults') {
        if (!isEmpty(computedDefault)) {
          obj[key] = computedDefault;
        }
      } // Else store computedDefault if it's a non-empty object(e.g. not {}) and satisfies certain conditions
      // Condition 1: If computedDefault is not empty or if the key is a required field
      // Condition 2: If the parent object is required or emptyObjectFields is not 'populateRequiredDefaults'
      else if ((!isEmpty(computedDefault) || requiredFields.includes(key)) && (isSelfOrParentRequired || emptyObjectFields !== 'populateRequiredDefaults')) {
        obj[key] = computedDefault;
      }
    } else if ( // Store computedDefault if it's a defined primitive (e.g., true) and satisfies certain conditions
    // Condition 1: computedDefault is not undefined
    // Condition 2: If emptyObjectFields is 'populateAllDefaults' or 'skipEmptyDefaults)
    // Or if isSelfOrParentRequired is 'true' and the key is a required field
    computedDefault !== undefined && (emptyObjectFields === 'populateAllDefaults' || emptyObjectFields === 'skipEmptyDefaults' || isSelfOrParentRequired && requiredFields.includes(key))) {
      obj[key] = computedDefault;
    }
  }
}
/** Computes the defaults for the current `schema` given the `rawFormData` and `parentDefaults` if any. This drills into
 * each level of the schema, recursively, to fill out every level of defaults provided by the schema.
 *
 * @param validator - an implementation of the `ValidatorType` interface that will be used when necessary
 * @param rawSchema - The schema for which the default state is desired
 * @param {ComputeDefaultsProps} computeDefaultsProps - Optional props for this function
 * @returns - The resulting `formData` with all the defaults provided
 */


export function computeDefaults(validator, rawSchema) {
  var computeDefaultsProps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var parentDefaults = computeDefaultsProps.parentDefaults,
      rawFormData = computeDefaultsProps.rawFormData,
      _computeDefaultsProps = computeDefaultsProps.rootSchema,
      rootSchema = _computeDefaultsProps === void 0 ? {} : _computeDefaultsProps,
      _computeDefaultsProps2 = computeDefaultsProps.includeUndefinedValues,
      includeUndefinedValues = _computeDefaultsProps2 === void 0 ? false : _computeDefaultsProps2,
      _computeDefaultsProps3 = computeDefaultsProps._recurseList,
      _recurseList = _computeDefaultsProps3 === void 0 ? [] : _computeDefaultsProps3,
      _computeDefaultsProps4 = computeDefaultsProps.experimental_defaultFormStateBehavior,
      experimental_defaultFormStateBehavior = _computeDefaultsProps4 === void 0 ? undefined : _computeDefaultsProps4,
      _computeDefaultsProps5 = computeDefaultsProps.experimental_customMergeAllOf,
      experimental_customMergeAllOf = _computeDefaultsProps5 === void 0 ? undefined : _computeDefaultsProps5,
      required = computeDefaultsProps.required,
      _computeDefaultsProps6 = computeDefaultsProps.shouldMergeDefaultsIntoFormData,
      shouldMergeDefaultsIntoFormData = _computeDefaultsProps6 === void 0 ? false : _computeDefaultsProps6;

  var formData = isObject(rawFormData) ? rawFormData : {};
  var schema = isObject(rawSchema) ? rawSchema : {}; // Compute the defaults recursively: give highest priority to deepest nodes.

  var defaults = parentDefaults; // If we get a new schema, then we need to recompute defaults again for the new schema found.

  var schemaToCompute = null;
  var experimental_dfsb_to_compute = experimental_defaultFormStateBehavior;
  var updatedRecurseList = _recurseList;

  if (schema[CONST_KEY] && (experimental_defaultFormStateBehavior === null || experimental_defaultFormStateBehavior === void 0 ? void 0 : experimental_defaultFormStateBehavior.constAsDefaults) !== 'never' && !constIsAjvDataReference(schema)) {
    defaults = schema[CONST_KEY];
  } else if (isObject(defaults) && isObject(schema.default)) {
    // For object defaults, only override parent defaults that are defined in
    // schema.default.
    defaults = mergeObjects(defaults, schema.default);
  } else if (DEFAULT_KEY in schema) {
    defaults = schema.default;
  } else if (REF_KEY in schema) {
    var refName = schema[REF_KEY]; // Use referenced schema defaults for this node.

    if (!_recurseList.includes(refName)) {
      updatedRecurseList = _recurseList.concat(refName);
      schemaToCompute = findSchemaDefinition(refName, rootSchema);
    }
  } else if (DEPENDENCIES_KEY in schema) {
    // Get the default if set from properties to ensure the dependencies conditions are resolved based on it
    var defaultFormData = _objectSpread(_objectSpread({}, getDefaultBasedOnSchemaType(validator, schema, computeDefaultsProps, defaults)), formData);

    var resolvedSchema = resolveDependencies(validator, schema, rootSchema, false, [], defaultFormData, experimental_customMergeAllOf);
    schemaToCompute = resolvedSchema[0]; // pick the first element from resolve dependencies
  } else if (isFixedItems(schema)) {
    defaults = schema.items.map(function (itemSchema, idx) {
      return computeDefaults(validator, itemSchema, {
        rootSchema: rootSchema,
        includeUndefinedValues: includeUndefinedValues,
        _recurseList: _recurseList,
        experimental_defaultFormStateBehavior: experimental_defaultFormStateBehavior,
        experimental_customMergeAllOf: experimental_customMergeAllOf,
        parentDefaults: Array.isArray(parentDefaults) ? parentDefaults[idx] : undefined,
        rawFormData: formData,
        required: required,
        shouldMergeDefaultsIntoFormData: shouldMergeDefaultsIntoFormData
      });
    });
  } else if (ONE_OF_KEY in schema) {
    var oneOf = schema.oneOf,
        remaining = _objectWithoutProperties(schema, _excluded);

    if (oneOf.length === 0) {
      return undefined;
    }

    var discriminator = getDiscriminatorFieldFromSchema(schema);
    var _remaining$type = remaining.type,
        type = _remaining$type === void 0 ? 'null' : _remaining$type;

    if (!Array.isArray(type) && PRIMITIVE_TYPES.includes(type) && (experimental_dfsb_to_compute === null || experimental_dfsb_to_compute === void 0 ? void 0 : experimental_dfsb_to_compute.constAsDefaults) === 'skipOneOf') {
      // If we are in a oneOf of a primitive type, then we want to pass constAsDefaults as 'never' for the recursion
      experimental_dfsb_to_compute = _objectSpread(_objectSpread({}, experimental_dfsb_to_compute), {}, {
        constAsDefaults: 'never'
      });
    }

    schemaToCompute = oneOf[getClosestMatchingOption(validator, rootSchema, rawFormData, oneOf, 0, discriminator, experimental_customMergeAllOf)];
    schemaToCompute = mergeSchemas(remaining, schemaToCompute);
  } else if (ANY_OF_KEY in schema) {
    var anyOf = schema.anyOf,
        _remaining = _objectWithoutProperties(schema, _excluded2);

    if (anyOf.length === 0) {
      return undefined;
    }

    var _discriminator = getDiscriminatorFieldFromSchema(schema);

    schemaToCompute = anyOf[getClosestMatchingOption(validator, rootSchema, rawFormData, anyOf, 0, _discriminator, experimental_customMergeAllOf)];
    schemaToCompute = mergeSchemas(_remaining, schemaToCompute);
  }

  if (schemaToCompute) {
    return computeDefaults(validator, schemaToCompute, {
      rootSchema: rootSchema,
      includeUndefinedValues: includeUndefinedValues,
      _recurseList: updatedRecurseList,
      experimental_defaultFormStateBehavior: experimental_dfsb_to_compute,
      experimental_customMergeAllOf: experimental_customMergeAllOf,
      parentDefaults: defaults,
      rawFormData: formData,
      required: required,
      shouldMergeDefaultsIntoFormData: shouldMergeDefaultsIntoFormData
    });
  } // No defaults defined for this node, fallback to generic typed ones.


  if (defaults === undefined) {
    defaults = schema.default;
  }

  var defaultBasedOnSchemaType = getDefaultBasedOnSchemaType(validator, schema, computeDefaultsProps, defaults);
  var defaultsWithFormData = defaultBasedOnSchemaType !== null && defaultBasedOnSchemaType !== void 0 ? defaultBasedOnSchemaType : defaults; // if shouldMergeDefaultsIntoFormData is true, then merge the defaults into the formData.

  if (shouldMergeDefaultsIntoFormData) {
    var _ref = experimental_defaultFormStateBehavior || {},
        _ref$arrayMinItems = _ref.arrayMinItems,
        arrayMinItems = _ref$arrayMinItems === void 0 ? {} : _ref$arrayMinItems;

    var mergeExtraDefaults = arrayMinItems.mergeExtraDefaults;
    var matchingFormData = ensureFormDataMatchingSchema(validator, schema, rootSchema, rawFormData, experimental_defaultFormStateBehavior);

    if (!isObject(rawFormData)) {
      defaultsWithFormData = mergeDefaultsWithFormData(defaultsWithFormData, matchingFormData, mergeExtraDefaults, true);
    }
  }

  return defaultsWithFormData;
}
/**
 * Ensure that the formData matches the given schema. If it's not matching in the case of a selectField, we change it to match the schema.
 *
 * @param validator - an implementation of the `ValidatorType` interface that will be used when necessary
 * @param schema - The schema for which the formData state is desired
 * @param rootSchema - The root schema, used to primarily to look up `$ref`s
 * @param formData - The current formData
 * @param experimental_defaultFormStateBehavior - Optional configuration object, if provided, allows users to override default form state behavior
 * @returns - valid formData that matches schema
 */

export function ensureFormDataMatchingSchema(validator, schema, rootSchema, formData, experimental_defaultFormStateBehavior) {
  var isSelectField = !isConstant(schema) && isSelect(validator, schema, rootSchema);
  var validFormData = formData;

  if (isSelectField) {
    var getOptionsList = optionsList(schema);
    var isValid = getOptionsList === null || getOptionsList === void 0 ? void 0 : getOptionsList.some(function (option) {
      return deepEquals(option.value, formData);
    });
    validFormData = isValid ? formData : undefined;
  } // Override the formData with the const if the constAsDefaults is set to always


  var constTakesPrecedence = schema[CONST_KEY] && (experimental_defaultFormStateBehavior === null || experimental_defaultFormStateBehavior === void 0 ? void 0 : experimental_defaultFormStateBehavior.constAsDefaults) === 'always';

  if (constTakesPrecedence) {
    validFormData = schema.const;
  }

  return validFormData;
}
/** Computes the default value for objects.
 *
 * @param validator - an implementation of the `ValidatorType` interface that will be used when necessary
 * @param rawSchema - The schema for which the default state is desired
 * @param {ComputeDefaultsProps} computeDefaultsProps - Optional props for this function
 * @param defaults - Optional props for this function
 * @returns - The default value based on the schema type if they are defined for object or array schemas.
 */

export function getObjectDefaults(validator, rawSchema) {
  var _ref2 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      rawFormData = _ref2.rawFormData,
      _ref2$rootSchema = _ref2.rootSchema,
      rootSchema = _ref2$rootSchema === void 0 ? {} : _ref2$rootSchema,
      _ref2$includeUndefine = _ref2.includeUndefinedValues,
      includeUndefinedValues = _ref2$includeUndefine === void 0 ? false : _ref2$includeUndefine,
      _ref2$_recurseList = _ref2._recurseList,
      _recurseList = _ref2$_recurseList === void 0 ? [] : _ref2$_recurseList,
      _ref2$experimental_de = _ref2.experimental_defaultFormStateBehavior,
      experimental_defaultFormStateBehavior = _ref2$experimental_de === void 0 ? undefined : _ref2$experimental_de,
      _ref2$experimental_cu = _ref2.experimental_customMergeAllOf,
      experimental_customMergeAllOf = _ref2$experimental_cu === void 0 ? undefined : _ref2$experimental_cu,
      required = _ref2.required,
      shouldMergeDefaultsIntoFormData = _ref2.shouldMergeDefaultsIntoFormData;

  var defaults = arguments.length > 3 ? arguments[3] : undefined;
  {
    var formData = isObject(rawFormData) ? rawFormData : {};
    var schema = rawSchema; // This is a custom addition that fixes this issue:
    // https://github.com/rjsf-team/react-jsonschema-form/issues/3832

    var retrievedSchema = (experimental_defaultFormStateBehavior === null || experimental_defaultFormStateBehavior === void 0 ? void 0 : experimental_defaultFormStateBehavior.allOf) === 'populateDefaults' && ALL_OF_KEY in schema ? retrieveSchema(validator, schema, rootSchema, formData, experimental_customMergeAllOf) : schema;
    var parentConst = retrievedSchema[CONST_KEY];
    var objectDefaults = Object.keys(retrievedSchema.properties || {}).reduce(function (acc, key) {
      var _a;

      var propertySchema = get(retrievedSchema, [PROPERTIES_KEY, key]); // Check if the parent schema has a const property defined AND we are supporting const as defaults, then we
      // should always return the computedDefault since it's coming from the const.

      var hasParentConst = isObject(parentConst) && parentConst[key] !== undefined;
      var hasConst = (isObject(propertySchema) && CONST_KEY in propertySchema || hasParentConst) && (experimental_defaultFormStateBehavior === null || experimental_defaultFormStateBehavior === void 0 ? void 0 : experimental_defaultFormStateBehavior.constAsDefaults) !== 'never' && !constIsAjvDataReference(propertySchema); // Compute the defaults for this node, with the parent defaults we might
      // have from a previous run: defaults[key].

      var computedDefault = computeDefaults(validator, propertySchema, {
        rootSchema: rootSchema,
        _recurseList: _recurseList,
        experimental_defaultFormStateBehavior: experimental_defaultFormStateBehavior,
        experimental_customMergeAllOf: experimental_customMergeAllOf,
        includeUndefinedValues: includeUndefinedValues === true,
        parentDefaults: get(defaults, [key]),
        rawFormData: get(formData, [key]),
        required: (_a = retrievedSchema.required) === null || _a === void 0 ? void 0 : _a.includes(key),
        shouldMergeDefaultsIntoFormData: shouldMergeDefaultsIntoFormData
      });
      maybeAddDefaultToObject(acc, key, computedDefault, includeUndefinedValues, required, retrievedSchema.required, experimental_defaultFormStateBehavior, hasConst);
      return acc;
    }, {});

    if (retrievedSchema.additionalProperties) {
      // as per spec additionalProperties may be either schema or boolean
      var additionalPropertiesSchema = isObject(retrievedSchema.additionalProperties) ? retrievedSchema.additionalProperties : {};
      var keys = new Set();

      if (isObject(defaults)) {
        Object.keys(defaults).filter(function (key) {
          return !retrievedSchema.properties || !retrievedSchema.properties[key];
        }).forEach(function (key) {
          return keys.add(key);
        });
      }

      var formDataRequired = [];
      Object.keys(formData).filter(function (key) {
        return !retrievedSchema.properties || !retrievedSchema.properties[key];
      }).forEach(function (key) {
        keys.add(key);
        formDataRequired.push(key);
      });
      keys.forEach(function (key) {
        var _a;

        var computedDefault = computeDefaults(validator, additionalPropertiesSchema, {
          rootSchema: rootSchema,
          _recurseList: _recurseList,
          experimental_defaultFormStateBehavior: experimental_defaultFormStateBehavior,
          experimental_customMergeAllOf: experimental_customMergeAllOf,
          includeUndefinedValues: includeUndefinedValues === true,
          parentDefaults: get(defaults, [key]),
          rawFormData: get(formData, [key]),
          required: (_a = retrievedSchema.required) === null || _a === void 0 ? void 0 : _a.includes(key),
          shouldMergeDefaultsIntoFormData: shouldMergeDefaultsIntoFormData
        }); // Since these are additional properties we don't need to add the `experimental_defaultFormStateBehavior` prop

        maybeAddDefaultToObject(objectDefaults, key, computedDefault, includeUndefinedValues, required, formDataRequired);
      });
    }

    return objectDefaults;
  }
}
/** Computes the default value for arrays.
 *
 * @param validator - an implementation of the `ValidatorType` interface that will be used when necessary
 * @param rawSchema - The schema for which the default state is desired
 * @param {ComputeDefaultsProps} computeDefaultsProps - Optional props for this function
 * @param defaults - Optional props for this function
 * @returns - The default value based on the schema type if they are defined for object or array schemas.
 */

export function getArrayDefaults(validator, rawSchema) {
  var _ref3 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      rawFormData = _ref3.rawFormData,
      _ref3$rootSchema = _ref3.rootSchema,
      rootSchema = _ref3$rootSchema === void 0 ? {} : _ref3$rootSchema,
      _ref3$_recurseList = _ref3._recurseList,
      _recurseList = _ref3$_recurseList === void 0 ? [] : _ref3$_recurseList,
      _ref3$experimental_de = _ref3.experimental_defaultFormStateBehavior,
      experimental_defaultFormStateBehavior = _ref3$experimental_de === void 0 ? undefined : _ref3$experimental_de,
      _ref3$experimental_cu = _ref3.experimental_customMergeAllOf,
      experimental_customMergeAllOf = _ref3$experimental_cu === void 0 ? undefined : _ref3$experimental_cu,
      required = _ref3.required,
      shouldMergeDefaultsIntoFormData = _ref3.shouldMergeDefaultsIntoFormData;

  var defaults = arguments.length > 3 ? arguments[3] : undefined;

  var _a, _b;

  var schema = rawSchema;
  var arrayMinItemsStateBehavior = (_a = experimental_defaultFormStateBehavior === null || experimental_defaultFormStateBehavior === void 0 ? void 0 : experimental_defaultFormStateBehavior.arrayMinItems) !== null && _a !== void 0 ? _a : {};
  var arrayMinItemsPopulate = arrayMinItemsStateBehavior.populate,
      arrayMergeExtraDefaults = arrayMinItemsStateBehavior.mergeExtraDefaults;
  var neverPopulate = arrayMinItemsPopulate === 'never';
  var ignoreMinItemsFlagSet = arrayMinItemsPopulate === 'requiredOnly';
  var isPopulateAll = arrayMinItemsPopulate === 'all' || !neverPopulate && !ignoreMinItemsFlagSet;
  var computeSkipPopulate = (_b = arrayMinItemsStateBehavior === null || arrayMinItemsStateBehavior === void 0 ? void 0 : arrayMinItemsStateBehavior.computeSkipPopulate) !== null && _b !== void 0 ? _b : function () {
    return false;
  };
  var isSkipEmptyDefaults = (experimental_defaultFormStateBehavior === null || experimental_defaultFormStateBehavior === void 0 ? void 0 : experimental_defaultFormStateBehavior.emptyObjectFields) === 'skipEmptyDefaults';
  var emptyDefault = isSkipEmptyDefaults ? undefined : []; // Inject defaults into existing array defaults

  if (Array.isArray(defaults)) {
    defaults = defaults.map(function (item, idx) {
      var schemaItem = getInnerSchemaForArrayItem(schema, AdditionalItemsHandling.Fallback, idx);
      return computeDefaults(validator, schemaItem, {
        rootSchema: rootSchema,
        _recurseList: _recurseList,
        experimental_defaultFormStateBehavior: experimental_defaultFormStateBehavior,
        experimental_customMergeAllOf: experimental_customMergeAllOf,
        parentDefaults: item,
        required: required,
        shouldMergeDefaultsIntoFormData: shouldMergeDefaultsIntoFormData
      });
    });
  } // Deeply inject defaults into already existing form data


  if (Array.isArray(rawFormData)) {
    var schemaItem = getInnerSchemaForArrayItem(schema);

    if (neverPopulate) {
      defaults = rawFormData;
    } else {
      var itemDefaults = rawFormData.map(function (item, idx) {
        return computeDefaults(validator, schemaItem, {
          rootSchema: rootSchema,
          _recurseList: _recurseList,
          experimental_defaultFormStateBehavior: experimental_defaultFormStateBehavior,
          experimental_customMergeAllOf: experimental_customMergeAllOf,
          rawFormData: item,
          parentDefaults: get(defaults, [idx]),
          required: required,
          shouldMergeDefaultsIntoFormData: shouldMergeDefaultsIntoFormData
        });
      }); // If the populate 'requiredOnly' flag is set then we only merge and include extra defaults if they are required.
      // Or if populate 'all' is set we merge and include extra defaults.

      var mergeExtraDefaults = (ignoreMinItemsFlagSet && required || isPopulateAll) && arrayMergeExtraDefaults;
      defaults = mergeDefaultsWithFormData(defaults, itemDefaults, mergeExtraDefaults);
    }
  } // Check if the schema has a const property defined AND we are supporting const as defaults, then we should always
  // return the computedDefault since it's coming from the const.


  var hasConst = isObject(schema) && CONST_KEY in schema && (experimental_defaultFormStateBehavior === null || experimental_defaultFormStateBehavior === void 0 ? void 0 : experimental_defaultFormStateBehavior.constAsDefaults) !== 'never';

  if (hasConst === false) {
    if (neverPopulate) {
      return defaults !== null && defaults !== void 0 ? defaults : emptyDefault;
    }

    if (ignoreMinItemsFlagSet && !required) {
      // If no form data exists or defaults are set leave the field empty/non-existent, otherwise
      // return form data/defaults
      return defaults ? defaults : undefined;
    }
  }

  var defaultsLength = Array.isArray(defaults) ? defaults.length : 0;

  if (!schema.minItems || isMultiSelect(validator, schema, rootSchema, experimental_customMergeAllOf) || computeSkipPopulate(validator, schema, rootSchema) || schema.minItems <= defaultsLength) {
    return defaults ? defaults : emptyDefault;
  }

  var defaultEntries = defaults || [];
  var fillerSchema = getInnerSchemaForArrayItem(schema, AdditionalItemsHandling.Invert);
  var fillerDefault = fillerSchema.default; // Calculate filler entries for remaining items (minItems - existing raw data/defaults)

  var fillerEntries = new Array(schema.minItems - defaultsLength).fill(computeDefaults(validator, fillerSchema, {
    parentDefaults: fillerDefault,
    rootSchema: rootSchema,
    _recurseList: _recurseList,
    experimental_defaultFormStateBehavior: experimental_defaultFormStateBehavior,
    experimental_customMergeAllOf: experimental_customMergeAllOf,
    required: required,
    shouldMergeDefaultsIntoFormData: shouldMergeDefaultsIntoFormData
  })); // then fill up the rest with either the item default or empty, up to minItems

  return defaultEntries.concat(fillerEntries);
}
/** Computes the default value based on the schema type.
 *
 * @param validator - an implementation of the `ValidatorType` interface that will be used when necessary
 * @param rawSchema - The schema for which the default state is desired
 * @param {ComputeDefaultsProps} computeDefaultsProps - Optional props for this function
 * @param defaults - Optional props for this function
 * @returns - The default value based on the schema type if they are defined for object or array schemas.
 */

export function getDefaultBasedOnSchemaType(validator, rawSchema) {
  var computeDefaultsProps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var defaults = arguments.length > 3 ? arguments[3] : undefined;

  switch (getSchemaType(rawSchema)) {
    // We need to recurse for object schema inner default values.
    case 'object':
      {
        return getObjectDefaults(validator, rawSchema, computeDefaultsProps, defaults);
      }

    case 'array':
      {
        return getArrayDefaults(validator, rawSchema, computeDefaultsProps, defaults);
      }
  }
}
/** Returns the superset of `formData` that includes the given set updated to include any missing fields that have
 * computed to have defaults provided in the `schema`.
 *
 * @param validator - An implementation of the `ValidatorType` interface that will be used when necessary
 * @param theSchema - The schema for which the default state is desired
 * @param [formData] - The current formData, if any, onto which to provide any missing defaults
 * @param [rootSchema] - The root schema, used to primarily to look up `$ref`s
 * @param [includeUndefinedValues=false] - Optional flag, if true, cause undefined values to be added as defaults.
 *          If "excludeObjectChildren", cause undefined values for this object and pass `includeUndefinedValues` as
 *          false when computing defaults for any nested object properties.
 * @param [experimental_defaultFormStateBehavior] Optional configuration object, if provided, allows users to override default form state behavior
 * @param [experimental_customMergeAllOf] - Optional function that allows for custom merging of `allOf` schemas
 * @returns - The resulting `formData` with all the defaults provided
 */

export default function getDefaultFormState(validator, theSchema, formData, rootSchema) {
  var includeUndefinedValues = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  var experimental_defaultFormStateBehavior = arguments.length > 5 ? arguments[5] : undefined;
  var experimental_customMergeAllOf = arguments.length > 6 ? arguments[6] : undefined;

  if (!isObject(theSchema)) {
    throw new Error('Invalid schema: ' + theSchema);
  }

  var schema = retrieveSchema(validator, theSchema, rootSchema, formData, experimental_customMergeAllOf); // Get the computed defaults with 'shouldMergeDefaultsIntoFormData' set to true to merge defaults into formData.
  // This is done when for example the value from formData does not exist in the schema 'enum' property, in such
  // cases we take the value from the defaults because the value from the formData is not valid.

  var defaults = computeDefaults(validator, schema, {
    rootSchema: rootSchema,
    includeUndefinedValues: includeUndefinedValues,
    experimental_defaultFormStateBehavior: experimental_defaultFormStateBehavior,
    experimental_customMergeAllOf: experimental_customMergeAllOf,
    rawFormData: formData,
    shouldMergeDefaultsIntoFormData: true
  }); // If the formData is an object or an array, add additional properties from formData and override formData with
  // defaults since the defaults are already merged with formData.

  if (isObject(formData) || Array.isArray(formData)) {
    var _ref4 = experimental_defaultFormStateBehavior || {},
        mergeDefaultsIntoFormData = _ref4.mergeDefaultsIntoFormData;

    var defaultSupercedesUndefined = mergeDefaultsIntoFormData === 'useDefaultIfFormDataUndefined';
    var result = mergeDefaultsWithFormData(defaults, formData, true, // set to true to add any additional default array entries.
    defaultSupercedesUndefined, true // set to true to override formData with defaults if they exist.
    );
    return result;
  }

  return defaults;
}